/* eslint-disable @typescript-eslint/naming-convention */
import { ContentChild, Directive, Input, OnDestroy, OnInit, Self } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { ReportOutputComponent } from '@ndt/report-output/report-output.component';
import { ReportOutputBase } from '@ndt/report-output/shared/models/report-output-base';
import { EMPTY, ReplaySubject, Subject } from 'rxjs';
import { switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ReportService } from '../services/report.service';
import { ReportComponent } from '../views/report-component';
import { ReportParametersDefDirective } from './report-parameters-def.directive';

// eslint-disable-next-line @typescript-eslint/naming-convention
export type ExtractData<R> = R extends ReportOutputBase<infer D, any> ? D : never;
// eslint-disable-next-line @typescript-eslint/naming-convention
export type ExtractParameters<R> = R extends ReportOutputBase<any, infer P> ? P : never;

@Directive({
    selector: '[ndtReportContainer]',
    standalone: false
})
// eslint-disable-next-line @typescript-eslint/naming-convention
export class ReportContainerDirective<
   R extends ReportOutputBase<any, any>,
   P extends { viewMode?: any } = ExtractParameters<R>
> implements OnDestroy, OnInit
{
   @ContentChild(ReportParametersDefDirective, { static: true })
   reportParametersDirective: ReportParametersDefDirective;

   @Input() forceHideNoReportDataFound = false;

   private reportOutputComponent$ = new ReplaySubject<ReportOutputComponent<R> | null>(1);

   private destroyed$ = new Subject<void>();

   private dialogId;

   private parametersDialogOpen = false;

   constructor(
      @Self() private reportComponent: ReportComponent,
      private reportService: ReportService<P, R>,
      private dialog: MatDialog,
      private dialogService: LcsDialogService
   ) {}

   ngOnInit(): void {
      this.reportComponent.showReportOutput = false;
      this.reportComponent.showNoReportDataFound = false;

      this.reportComponent.configure.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         this.openParametersDialog();
      });
      // this.reportComponent.saveParameters.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      //    this.openSaveDialog();
      // });
      this.reportService.allowExcel$.pipe(takeUntil(this.destroyed$)).subscribe((allowExcel: boolean) => {
         this.reportComponent.allowExcel = allowExcel;
      });
      this.reportService.allowHtml$.pipe(takeUntil(this.destroyed$)).subscribe((allowHtml: boolean) => {
         this.reportComponent.allowHtml = allowHtml;
      });
      this.reportService.allowPdf$.pipe(takeUntil(this.destroyed$)).subscribe((allowPdf: boolean) => {
         this.reportComponent.allowPdf = allowPdf;
      });
      this.reportService.allowMemorize$.pipe(takeUntil(this.destroyed$)).subscribe((allowMemorize: boolean) => {
         this.reportComponent.allowMemorize = allowMemorize;
      });
      this.reportComponent.downloadExcel.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         return this.reportService.onDownloadExcelClick();
      });
      this.reportComponent.downloadPdf.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         return this.reportService.onDownloadPdfClick();
      });
      this.reportComponent.downloadHtml.pipe(takeUntil(this.destroyed$)).subscribe(() => {
         return this.reportService.onDownloadHtmlClick();
      });

      this.reportService.callState$.pipe(takeUntil(this.destroyed$)).subscribe((callState: CallState) => {
         this.reportComponent.callState = callState;
      });
      this.reportService.reportName$.pipe(takeUntil(this.destroyed$)).subscribe((reportName: string) => {
         this.reportComponent.name = reportName;
      });
      this.reportOutputComponent$
         .pipe(
            switchMap((roc) => {
               return roc?.outputContainer$ ?? EMPTY;
            }),
            switchMap((oc) => {
               return oc.viewModeChange;
            })
         )
         .subscribe((viewMode) => {
            this.reportService.onViewModeChanged(viewMode);
         });
      this.reportService.data$
         .pipe(withLatestFrom(this.reportOutputComponent$), takeUntil(this.destroyed$))
         .subscribe(([report, outputComponent]: [R | null, ReportOutputComponent<R> | null]) => {
            if (outputComponent) {
               outputComponent.setReportData(report);
               if (report) {
                  this.reportComponent.showReportOutput = true;
                  this.closeParametersDialog();
               } else {
                  this.reportComponent.showReportOutput = false;
               }
            }
         });
      this.reportService.showParametersDialog$.subscribe((value) => {
         if (value && !this.parametersDialogOpen) {
            this.dialog.closeAll();
            this.openParametersDialog();
         }
      });
   }

   ngOnDestroy(): void {
      this.closeParametersDialog();
      this.destroyed$.next();
   }

   registerOutputComponent(reportOutputComponent: ReportOutputComponent<R> | null): void {
      this.reportOutputComponent$.next(reportOutputComponent);
   }

   private openParametersDialog(): void {
      this.parametersDialogOpen = true;
      const ref = this.dialog.open(this.reportParametersDirective.templateRef, {
         disableClose: true,
         panelClass: [DialogSize.Medium, 'dialog-container'],
      });
      // matDialog generates a unique id if none is given to it
      this.dialogId = ref.id;
      // Show the "No report data found" message only after the user closes the report parameters dialog.
      // Otherwise, the message will be visible underneath the parameters dialog on reports with few parameters.
      ref.afterClosed().subscribe(() => {
         this.reportComponent.showNoReportDataFound = !this.forceHideNoReportDataFound;
         this.parametersDialogOpen = false;
      });
   }

   // private openSaveDialog(): void {
   //    this.dialogService.open(SaveReportParametersComponent, {
   //       disableClose: true,
   //       panelClass: [DialogSize.Medium, 'dialog-container'],
   //    });
   // }

   private closeParametersDialog(): void {
      this.dialog.getDialogById(this.dialogId)?.close();
   }
}
